import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { AxiosError } from 'axios';
import {
  Facebook,
  Chrome,
  Send,
  Shield,
  Loader2,
  MessageSquare,
  Heart,
  ChevronDown,
} from 'lucide-react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { testimonialService as _, verifyWithFacebook, verifyWithGoogle, type SocialProfile } from '@services/testimonial.service';
import { useCampaignStore } from '@stores/campaign.store';
import { useAnalytics } from '@hooks/useAnalytics';
import type { Testimonial } from '@/types';

// -----------------------------------------------------------------
// Animations
// -----------------------------------------------------------------

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 } as const,
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } as const },
  hover: { scale: 1.02, y: -4, transition: { duration: 0.2 } as const },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4 } as const },
};

// -----------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function getProviderIcon(providerName: string) {
  switch (providerName.toLowerCase()) {
    case 'facebook':
      return <Facebook className="text-blue-600 w-4 h-4" />;
    case 'google':
      return <Chrome className="text-red-500 w-4 h-4" />;
    default:
      return <Shield className="text-gray-500 w-4 h-4" />;
  }
}

// -----------------------------------------------------------------
// Testimonial Card Component
// -----------------------------------------------------------------

function TestimonialCard({ testimonial }: { testimonial: Testimonial }) {
  return (
    <motion.div
      variants={cardVariants}
      whileHover="hover"
      initial="hidden"
      animate="visible"
      className={`relative bg-white rounded-2xl shadow-lg p-6 border-2 transition-all duration-300 ${
        testimonial.isPinned
          ? 'border-timor-yellow ring-2 ring-timor-yellow/20'
          : 'border-gray-100 hover:border-sky-200'
      }`}
    >
      {testimonial.isPinned && (
        <div className="absolute -top-3 left-6">
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-timor-yellow text-campaign-black shadow-md">
            <Heart className="w-3 h-3" />
            Featured
          </span>
        </div>
      )}

      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {testimonial.profilePictureUrl ? (
            <img
              src={testimonial.profilePictureUrl}
              alt={testimonial.displayName}
              className="w-12 h-12 rounded-full object-cover border-2 border-gray-200"
              loading="lazy"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center text-white font-heading font-bold text-lg shadow-md">
              {testimonial.displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-heading font-bold text-campaign-black text-sm truncate">
              {testimonial.displayName}
            </span>
            {getProviderIcon(testimonial.providerName)}
          </div>
          <p className="text-xs text-gray-400 mb-3">{formatDate(testimonial.createdAt)}</p>
          <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
            {testimonial.message}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

// -----------------------------------------------------------------
// Main Page Component
// -----------------------------------------------------------------

export function Testimonials() {
  const { t } = useTranslation();
  const { trackFormSubmission } = useAnalytics();
  const { addNotification } = useCampaignStore();

  // Social auth state
  const [verifiedUser, setVerifiedUser] = useState<SocialProfile | null>(null);
  const [isVerifying, setIsVerifying] = useState<string | null>(null); // 'facebook' | 'google' | null

  // Form state
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const MAX_CHARS = 2000;

  // Testimonial list state
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalTestimonials, setTotalTestimonials] = useState(0);

  const MOCK_TESTIMONIALS: Testimonial[] = [
    {
      id: 'mock-001',
      providerName: 'facebook',
      providerUserId: 'fb-10001',
      displayName: 'Maria Fernandes',
      profilePictureUrl: null,
      message:
        'Dr. Rui is a true servant of our nation. His dedication to health and education gives me hope for a better Timor-Leste. 💙',
      isPinned: true,
      createdAt: new Date(Date.now() - 3600_000).toISOString(),
    },
    {
      id: 'mock-002',
      providerName: 'google',
      providerUserId: 'g-20001',
      displayName: 'José da Costa',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=11',
      message:
        'I remember when Dr. Rui visited our suco in Baucau. He listened to everyone. This is the kind of leader we need.',
      isPinned: true,
      createdAt: new Date(Date.now() - 7200_000).toISOString(),
    },
    {
      id: 'mock-003',
      providerName: 'facebook',
      providerUserId: 'fb-10002',
      displayName: 'Ana Martins',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=5',
      message:
        'Tempu to\'o ona! We stand with you, Dr. Rui. Your vision for unity and progress is exactly what our country needs right now.',
      isPinned: false,
      createdAt: new Date(Date.now() - 86400_000).toISOString(),
    },
    {
      id: 'mock-004',
      providerName: 'google',
      providerUserId: 'g-20002',
      displayName: 'Carlos Ximenes',
      profilePictureUrl: null,
      message:
        'From Clandestine Resistance to the Prime Minister\'s office — Dr. Rui has always walked with the people. I am proud to support this campaign.',
      isPinned: false,
      createdAt: new Date(Date.now() - 172800_000).toISOString(),
    },
    {
      id: 'mock-005',
      providerName: 'facebook',
      providerUserId: 'fb-10003',
      displayName: 'Beatriz dos Santos',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=9',
      message:
        'My grandmother always said: "A good doctor heals with the heart." That is Dr. Rui. Obrigado ba Ita-nia servisu hotu!',
      isPinned: false,
      createdAt: new Date(Date.now() - 259200_000).toISOString(),
    },
    {
      id: 'mock-006',
      providerName: 'google',
      providerUserId: 'g-20003',
      displayName: 'Tome de Jesus',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=15',
      message:
        'We need leaders who understand the reality of the people in the aldeias. Dr. Rui has walked those roads with us. Vote Rui 2027! 🇹🇱',
      isPinned: false,
      createdAt: new Date(Date.now() - 345600_000).toISOString(),
    },
    {
      id: 'mock-007',
      providerName: 'facebook',
      providerUserId: 'fb-10004',
      displayName: 'Luís Sarmento',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=33',
      message:
        'As a young Timorese studying abroad, this campaign gives me hope that our country is moving in the right direction. Count my support!',
      isPinned: false,
      createdAt: new Date(Date.now() - 432000_000).toISOString(),
    },
    {
      id: 'mock-008',
      providerName: 'google',
      providerUserId: 'g-20004',
      displayName: 'Filomena Belo',
      profilePictureUrl: null,
      message:
        'Unity above politics. That is what Dr. Rui stands for. I want my children to grow up in a Timor-Leste that values meritocracy and compassion.',
      isPinned: false,
      createdAt: new Date(Date.now() - 518400_000).toISOString(),
    },
    {
      id: 'mock-009',
      providerName: 'facebook',
      providerUserId: 'fb-10005',
      displayName: 'António da Silva',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=51',
      message:
        'It is rare to find a leader who has actually lived the struggles of our people. Dr. Rui knows what it means to sacrifice for Timor-Leste.',
      isPinned: false,
      createdAt: new Date(Date.now() - 604800_000).toISOString(),
    },
    {
      id: 'mock-010',
      providerName: 'google',
      providerUserId: 'g-20005',
      displayName: 'Mariana Gusmão',
      profilePictureUrl:
        'https://i.pravatar.cc/128?img=23',
      message:
        'I have followed Dr. Rui since he was Minister of Health. He never forgot where he came from. That is integrity. That is leadership.',
      isPinned: false,
      createdAt: new Date(Date.now() - 691200_000).toISOString(),
    },
  ];

  // Load testimonials on mount
  useEffect(() => {
    loadTestimonials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTestimonials = async (pageNumber = 1, append = false) => {
    try {
      if (pageNumber === 1) {
        setIsLoading(true);
      } else {
        setIsLoadingMore(true);
      }

      // --- MOCK DATA — comment out this block when real API is ready ---
      await new Promise((resolve) => setTimeout(resolve, 1200)); // simulate loading
      const mockResult = {
        items: append ? MOCK_TESTIMONIALS : MOCK_TESTIMONIALS.slice(0, 6),
        totalCount: MOCK_TESTIMONIALS.length,
        page: pageNumber,
        pageSize: 10,
        totalPages: Math.ceil(MOCK_TESTIMONIALS.length / 10),
      };
      if (append) {
        setTestimonials((prev) => [...prev, ...mockResult.items]);
      } else {
        setTestimonials(mockResult.items);
      }
      setTotalTestimonials(mockResult.totalCount);
      setPage(pageNumber);
      setHasMore(pageNumber < mockResult.totalPages);
      // --- END MOCK DATA ---
    } catch (err) {
      console.error('Failed to load testimonials:', err);
      addNotification({
        type: 'error',
        message: t('testimonials.loadError'),
      });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  };

  const handleVerifyFacebook = useCallback(async () => {
    setIsVerifying('facebook');
    try {
      const profile = await verifyWithFacebook();
      setVerifiedUser(profile);
      addNotification({
        type: 'success',
        message: t('testimonials.verifiedSuccess', { provider: 'Facebook' }),
      });
    } catch (err) {
      addNotification({
        type: 'error',
        message: (err as Error).message || t('testimonials.verifyError'),
      });
    } finally {
      setIsVerifying(null);
    }
  }, [addNotification, t]);

  const handleVerifyGoogle = useCallback(async () => {
    setIsVerifying('google');
    try {
      const profile = await verifyWithGoogle();
      setVerifiedUser(profile);
      addNotification({
        type: 'success',
        message: t('testimonials.verifiedSuccess', { provider: 'Google' }),
      });
    } catch (err) {
      addNotification({
        type: 'error',
        message: (err as Error).message || t('testimonials.verifyError'),
      });
    } finally {
      setIsVerifying(null);
    }
  }, [addNotification, t]);

  const handleLogout = () => {
    setVerifiedUser(null);
    setMessage('');
    setCharCount(0);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    if (val.length <= MAX_CHARS) {
      setMessage(val);
      setCharCount(val.length);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!verifiedUser) {
      addNotification({
        type: 'error',
        message: t('testimonials.verifyRequired'),
      });
      return;
    }

    if (message.trim().length < 10) {
      addNotification({
        type: 'error',
        message: t('testimonials.messageTooShort'),
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // --- MOCK POST — comment out this block when real API is ready ---
      await new Promise((resolve) => setTimeout(resolve, 800));
      const newTestimonial: Testimonial = {
        id: `mock-new-${Date.now()}`,
        providerName: verifiedUser.providerName,
        providerUserId: verifiedUser.providerUserId,
        displayName: verifiedUser.displayName,
        profilePictureUrl: verifiedUser.profilePictureUrl,
        message: message.trim(),
        isPinned: false,
        createdAt: new Date().toISOString(),
      };
      setTestimonials((prev) => [newTestimonial, ...prev]);
      setTotalTestimonials((c) => c + 1);
      // --- END MOCK POST ---

      trackFormSubmission('testimonial_post', true);

      addNotification({
        type: 'success',
        message: t('testimonials.success'),
      });

      // Reset form and refresh list
      setMessage('');
      setCharCount(0);
      await loadTestimonials(1, false);
    } catch (err) {
      trackFormSubmission('testimonial_post', false);

      if (err instanceof AxiosError) {
        const data = err.response?.data as { message?: string; title?: string };
        const errorMsg = data?.message || data?.title || t('testimonials.error');

        if (err.response?.status === 409) {
          addNotification({
            type: 'error',
            message: t('testimonials.duplicate'),
          });
        } else {
          addNotification({
            type: 'error',
            message: errorMsg,
          });
        }
      } else {
        addNotification({
          type: 'error',
          message: t('testimonials.error'),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen bg-gradient-to-b from-sky-50 via-white to-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ---- Header ---- */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-700 text-sm font-semibold mb-4">
            <MessageSquare className="w-4 h-4" />
            {t('testimonials.badge')}
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-campaign-black mb-4">
            {t('testimonials.title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            {t('testimonials.subtitle')}
          </p>
        </motion.div>

        {/* ---- Verification + Post Form ---- */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mb-12"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Card Header */}
            <div className="bg-gradient-to-r from-sky-500 to-sky-600 px-8 py-6">
              <h2 className="text-2xl font-heading font-bold text-white mb-1">
                {t('testimonials.postTitle')}
              </h2>
              <p className="text-sky-100 text-sm">
                {t('testimonials.postSubtitle')}
              </p>
            </div>

            <div className="px-8 py-6">
              {/* Social Verification Buttons */}
              {!verifiedUser && (
                <div className="mb-6">
                  <p className="text-sm font-semibold text-campaign-black mb-3">
                    {t('testimonials.verifyLabel')}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={handleVerifyFacebook}
                      disabled={isVerifying !== null}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-[#1877F2] text-white font-semibold text-sm hover:bg-[#166fe5] active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                    >
                      {isVerifying === 'facebook' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Facebook className="w-5 h-5" />
                      )}
                      {isVerifying === 'facebook'
                        ? t('testimonials.verifying')
                        : t('testimonials.verifyWith', { provider: 'Facebook' })}
                    </button>

                    <button
                      type="button"
                      onClick={handleVerifyGoogle}
                      disabled={isVerifying !== null}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-gray-700 font-semibold text-sm hover:bg-gray-50 active:scale-95 transition-all duration-200 border-2 border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg"
                    >
                      {isVerifying === 'google' ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Chrome className="w-5 h-5" />
                      )}
                      {isVerifying === 'google'
                        ? t('testimonials.verifying')
                        : t('testimonials.verifyWith', { provider: 'Google' })}
                    </button>
                  </div>
                </div>
              )}

              {/* Verified User Badge */}
              {verifiedUser && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 rounded-xl bg-green-50 border border-green-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {verifiedUser.profilePictureUrl ? (
                        <img
                          src={verifiedUser.profilePictureUrl}
                          alt={verifiedUser.displayName}
                          className="w-10 h-10 rounded-full object-cover border-2 border-green-300"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-heading font-bold">
                          {verifiedUser.displayName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div>
                        <p className="font-semibold text-green-800 text-sm flex items-center gap-1">
                          <Shield className="w-3.5 h-3.5" />
                          {t('testimonials.verifiedAs', {
                            provider: verifiedUser.providerName,
                          })}
                        </p>
                        <p className="text-green-700 font-bold text-sm">
                          {verifiedUser.displayName}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="text-xs text-green-600 hover:text-green-800 font-medium underline"
                    >
                      {t('testimonials.changeAccount')}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Message Form */}
              <form onSubmit={handleSubmit}>
                <label className="block text-sm font-semibold text-campaign-black mb-2">
                  {t('testimonials.messageLabel')}
                </label>
                <div className="relative">
                  <textarea
                    value={message}
                    onChange={handleMessageChange}
                    required
                    rows={5}
                    maxLength={MAX_CHARS}
                    placeholder={t('testimonials.messagePlaceholder')}
                    disabled={!verifiedUser}
                    className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-sky-500 focus:outline-none resize-none disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
                  />
                  <div className="absolute bottom-3 right-3 text-xs text-gray-400">
                    {charCount}/{MAX_CHARS}
                  </div>
                </div>

                {!verifiedUser && (
                  <p className="mt-2 text-xs text-amber-600 font-medium">
                    {t('testimonials.verifyRequiredHint')}
                  </p>
                )}

                <motion.button
                  type="submit"
                  whileHover={!isSubmitting && verifiedUser ? { scale: 1.02 } : {}}
                  whileTap={!isSubmitting && verifiedUser ? { scale: 0.98 } : {}}
                  disabled={!verifiedUser || isSubmitting || message.trim().length < 10}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-campaign-black text-white font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-800 active:scale-95 transition-all duration-200 shadow-lg"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      {t('testimonials.posting...')}
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      {t('testimonials.submit')}
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </div>
        </motion.div>

        {/* ---- Testimonials List ---- */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-heading font-bold text-campaign-black">
              {t('testimonials.listTitle')}
            </h2>
            {totalTestimonials > 0 && (
              <span className="text-sm text-gray-500 font-medium">
                {totalTestimonials} {t('testimonials.messageCount')}
              </span>
            )}
          </div>

          {/* Loading Skeleton */}
          {isLoading && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl shadow-lg p-6 border-2 border-gray-100 animate-pulse">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-32" />
                      <div className="h-3 bg-gray-100 rounded w-20" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && testimonials.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-16"
            >
              <div className="text-6xl mb-4">💬</div>
              <h3 className="text-xl font-heading font-bold text-campaign-black mb-2">
                {t('testimonials.emptyTitle')}
              </h3>
              <p className="text-gray-500">
                {t('testimonials.emptySubtitle')}
              </p>
            </motion.div>
          )}

          {/* Testimonials Grid */}
          <AnimatePresence>
            {testimonials.length > 0 && (
              <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {testimonials.map((testimonial) => (
                  <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Load More */}
          {hasMore && testimonials.length > 0 && (
            <div className="text-center mt-10">
              <button
                onClick={() => loadTestimonials(page + 1, true)}
                disabled={isLoadingMore}
                className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-white text-campaign-black font-semibold text-sm border-2 border-gray-200 hover:border-sky-400 hover:text-sky-600 active:scale-95 transition-all duration-200 shadow-md disabled:opacity-50"
              >
                {isLoadingMore ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('testimonials.loading')}
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-4 h-4" />
                    {t('testimonials.loadMore')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
